import { useEffect, useRef, useState, useCallback } from 'react';
import { 
  WebSocketClient, 
  WebSocketClientOptions, 
  ConnectionStatus, 
  ClientMessage, 
  ServerMessage, 
  TranscriptionMessage 
} from '../lib/websocketClient';

export interface UseWebSocketOptions extends Omit<WebSocketClientOptions, 'url'> {
  autoConnect?: boolean;
}

export interface UseWebSocketReturn {
  client: WebSocketClient | null;
  status: ConnectionStatus;
  connect: () => Promise<void>;
  disconnect: () => void;
  sendMessage: (message: ClientMessage) => boolean;
  isConnected: boolean;
  error: Error | null;
  lastMessage: ServerMessage | null;
  lastTranscription: TranscriptionMessage | null;
}

export function useWebSocket(
  url: string | null, 
  options: UseWebSocketOptions = {}
): UseWebSocketReturn {
  const { autoConnect = true, ...clientOptions } = options;
  
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [error, setError] = useState<Error | null>(null);
  const [lastMessage, setLastMessage] = useState<ServerMessage | null>(null);
  const [lastTranscription, setLastTranscription] = useState<TranscriptionMessage | null>(null);
  
  const clientRef = useRef<WebSocketClient | null>(null);
  const isConnected = status === 'connected';

  // Initialize client when URL changes
  useEffect(() => {
    if (!url) {
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }
      return;
    }

    // Create new client
    const client = new WebSocketClient({
      url,
      ...clientOptions
    });

    // Set up event handlers
    client.on('onStatusChange', (newStatus) => {
      setStatus(newStatus);
      if (newStatus !== 'error') {
        setError(null);
      }
    });

    client.on('onError', (err) => {
      setError(err);
    });

    client.on('onMessage', (message) => {
      setLastMessage(message);
    });

    client.on('onTranscription', (transcription) => {
      setLastTranscription(transcription);
    });

    // Assign client to ref immediately
    clientRef.current = client;

    // Auto-connect if enabled
    if (autoConnect) {
      client.connect().catch((err) => {
        console.error('Auto-connect failed:', err);
      });
    }

    // Cleanup on unmount or URL change
    return () => {
      client.disconnect();
    };
  }, [url, autoConnect, JSON.stringify(clientOptions)]);

  const connect = useCallback(async () => {
    if (!clientRef.current) {
      throw new Error('WebSocket client not initialized');
    }
    await clientRef.current.connect();
  }, []);

  const disconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.disconnect();
    }
  }, []);

  const sendMessage = useCallback((message: ClientMessage): boolean => {
    if (!clientRef.current) {
      return false;
    }
    return clientRef.current.sendMessage(message);
  }, []);

  return {
    client: clientRef.current,
    status,
    connect,
    disconnect,
    sendMessage,
    isConnected,
    error,
    lastMessage,
    lastTranscription
  };
}

// Specialized hooks for different use cases
export function useStreamerWebSocket(roomId: string | null, options: UseWebSocketOptions = {}) {
  const baseWsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8787';
  const wsUrl = roomId ? `${baseWsUrl}/ws/${roomId}` : null;
  const webSocket = useWebSocket(wsUrl, options);

  const startStream = useCallback(() => {
    if (!roomId) return false;
    
    return webSocket.sendMessage({
      type: 'start_stream',
      roomId
    });
  }, [roomId, webSocket]);

  const sendAudioChunk = useCallback((audioData: ArrayBuffer, timestamp: number, sequenceId: number) => {
    return webSocket.sendMessage({
      type: 'audio_chunk',
      data: audioData,
      timestamp,
      sequenceId
    });
  }, [webSocket]);

  return {
    ...webSocket,
    startStream,
    sendAudioChunk
  };
}

export function useViewerWebSocket(roomId: string | null, options: UseWebSocketOptions = {}) {
  const baseWsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8787';
  const wsUrl = roomId ? `${baseWsUrl}/ws/${roomId}` : null;
  const webSocket = useWebSocket(wsUrl, options);

  const joinRoom = useCallback(() => {
    if (!roomId) return false;
    
    return webSocket.sendMessage({
      type: 'join_room',
      roomId
    });
  }, [roomId, webSocket]);

  // Auto-join room when connected
  useEffect(() => {
    if (webSocket.isConnected && roomId) {
      joinRoom();
    }
  }, [webSocket.isConnected, roomId, joinRoom]);

  return {
    ...webSocket,
    joinRoom
  };
}